// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import type { WarningKind } from "@/lib/expression-validator";

interface PredicateWarningProps {
  /** The short warning message (without any inlined value list). */
  message: string;
  /** Which kind of warning this is — selects the label. Defaults to the unknown-property case. */
  kind?: WarningKind;
  /**
   * The chip row's contents — an enum mismatch's accepted values, or an object's field names.
   * Rendered as a compact, wrapping chip row.
   */
  options?: readonly string[];
}

// Each warning kind gets its own label — inferring it from the payload mislabels any warning that
// carries no accepted-value set but isn't about an unrecognised property.
const LABEL_KEYS: Record<WarningKind, string> = {
  property: "octoRules.edit.predicateUnknownProperty",
  value: "octoRules.edit.predicateInvalidValue",
  decimal: "octoRules.edit.predicateDecimalExpected",
  arithmetic: "octoRules.edit.predicateRuntimeTypeError",
  object: "octoRules.edit.predicateObjectNotValue",
};

/**
 * Non-blocking predicate warning shown under the CEL editor. The message stays on one line; when the
 * warning carries a value set the entries render as a wrapping chip row (same treatment as the
 * "Variables" reference row) — far more scannable than an inlined list.
 */
export function PredicateWarning({ message, kind = "property", options }: PredicateWarningProps) {
  const { t } = useTranslation("back-office");
  const hasOptions = !!options && options.length > 0;
  const label = t(LABEL_KEYS[kind]);
  // For an object warning the chips are the fields to pick from, not values the expression may use.
  const optionsLabel = kind === "object" ? t("octoRules.edit.availableFields") : t("octoRules.edit.allowedValues");
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm text-amber-600 dark:text-amber-500">
        {label} — {message}
      </span>
      {hasOptions && (
        <div className="flex items-start gap-1.5 text-sm text-amber-600 dark:text-amber-500">
          <span className="shrink-0 py-0.5">{optionsLabel}:</span>
          <div className="flex flex-wrap gap-1.5">
            {options.map((option) => (
              <code
                key={option}
                className="rounded border border-amber-200 bg-amber-100 px-1.5 py-0.5 font-mono text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
              >
                {option}
              </code>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
